import PropTypes from 'prop-types'
import includes from 'lodash/includes'
import isEqual from 'lodash/isEqual'
import map from 'lodash/map'
import size from 'lodash/size'
import trim from 'lodash/trim'
import values from 'lodash/values'

import {CONTEXT} from '../../sagas/loginForms'
import {isEmailAddress, isTenDigitPhoneNumber, isValidAddressLength} from '../../utils'
import {MIN_PASSWORD_CHAR_LENGTH} from '../../const'

export const contextPropType = PropTypes.oneOf(values(CONTEXT)).isRequired

export const ADDRESS_1_FIELD = 'address1'
export const ADDRESS_2_FIELD = 'address2'
export const TO_BUSINESS_ADDRESS_FIELD = 'toBusinessAddress'
export const CITY_FIELD = 'city'
export const EMAIL_FIELD = 'email'
export const EVENT_LOOK_FIELD = 'eventLook'
export const FIRST_NAME_FIELD = 'firstName'
export const LAST_NAME_FIELD = 'lastName'
export const PASSWORD_FIELD = 'password'
export const PASSWORD_CONFIRMATION_FIELD = 'passwordConfirmation'
export const PHONE_FIELD = 'phone'
export const STATE_FIELD = 'state'
export const ZIP_FIELD = 'zip'
export const ADDRESS_FIELDS = [ADDRESS_1_FIELD, ADDRESS_2_FIELD, CITY_FIELD, STATE_FIELD, ZIP_FIELD]
export const NON_NULLABLE_FULL_ADDRESS_FIELDS = [
  ADDRESS_1_FIELD,
  CITY_FIELD,
  STATE_FIELD,
  ZIP_FIELD,
  FIRST_NAME_FIELD,
  LAST_NAME_FIELD,
  PHONE_FIELD,
]

export const validateAgainstDupeEmail = (obj, items) => {
  // get each email address and put into new array
  const emailArray = map(items, 'emailAddress')
  // if submitted email matches any from new array, return error
  if (includes(emailArray, obj.email)) {
    return {email: 'Duplicate email address.'}
  }
  return {}
}

export const validateEmail = (obj) => {
  if (!obj[EMAIL_FIELD]) {
    return {[EMAIL_FIELD]: 'Missing email.'}
  }
  if (!isEmailAddress(obj[EMAIL_FIELD])) {
    return {[EMAIL_FIELD]: 'Invalid email address.'}
  }
  return {}
}

export const validateEventLook = (obj) => {
  if (!obj[EVENT_LOOK_FIELD]) {
    return {[EVENT_LOOK_FIELD]: 'Missing event look.'}
  }
  return {}
}

export const validateFirstName = (obj) => {
  if (!trim(obj[FIRST_NAME_FIELD])) {
    return {[FIRST_NAME_FIELD]: 'Missing first name.'}
  }
  return {}
}

export const validateLastName = (obj) => {
  if (!trim(obj[LAST_NAME_FIELD])) {
    return {[LAST_NAME_FIELD]: 'Missing last name.'}
  }
  return {}
}

export const validatePassword = (obj) => {
  if (!obj[PASSWORD_FIELD]) {
    return {[PASSWORD_FIELD]: 'Missing password.'}
  }
  if (size(obj[PASSWORD_FIELD]) < MIN_PASSWORD_CHAR_LENGTH) {
    return {[PASSWORD_FIELD]: 'Password must be at least 6 characters long.'}
  }
  return {}
}

export const validatePasswordConfirmation = (obj) => {
  if (!obj[PASSWORD_CONFIRMATION_FIELD]) {
    return {[PASSWORD_CONFIRMATION_FIELD]: 'Missing password confirmation.'}
  }
  if (!isEqual(obj[PASSWORD_FIELD], obj[PASSWORD_CONFIRMATION_FIELD])) {
    return {[PASSWORD_CONFIRMATION_FIELD]: 'Passwords do not match'}
  }
  return {}
}

export const validateEmailAndPassword = (obj) => ({
  ...validateEmail(obj),
  ...validatePassword(obj),
})

export const required = (value) => (!trim(value) ? 'Missing required field.' : undefined)

export const validZip = (value) =>
  value.length !== 5 && value.length !== 10 ? 'Zip code is improperly formatted.' : undefined

export const validPhone = (value) =>
  isTenDigitPhoneNumber(value) ? undefined : 'Phone number must be 10 digits in length.'

export const validShippingAddressLength = (value) => {
  if (value) {
    return isValidAddressLength(value) ? undefined : 'Address cannot be greater than 25 characters.'
  }
  return undefined
}

const stripNonAlphanumeric = (s) => s.replace(/\W/g, '')

// Regex to recognize PO Boxes - matches the regex used on the server side from lionheart.validators.po_box_validator
const poBoxRegex = /p\.? *o\.? .+\d+/i

// Regexes to recognize military addresses. Has a special case to allow an address like "Unit 123", which could
// be a military address, but could also be a condo dweller who likes to put the unit number on the first line.
const militaryStreetAddressRegex = /^((apo|cmr|omdc|psc|rpo|umr)\d+)|(unit\d+box)/i
const militaryCityRegex = /^(apo|fpo|dpo)$/i
const militaryShippingError = 'Unfortunately, we are unable to ship to military addresses.'

export const validStreetAddress = (value) => {
  if (militaryStreetAddressRegex.test(stripNonAlphanumeric(value))) {
    return militaryShippingError
  }
  if (poBoxRegex.test(value)) {
    return 'Unfortunately, we are unable to ship to PO Boxes.'
  }
  return validShippingAddressLength(value)
}

export const validCity = (value) =>
  militaryCityRegex.test(stripNonAlphanumeric(value)) ? militaryShippingError : undefined

export const validState = (value) =>
  ['AK', 'HI'].includes(value) ? 'We do not currently ship to this location.' : undefined

export const formatPhone = (phone) => phone && phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')

export const formatZip = (zip) =>
  zip && (zip.length <= 5 ? zip : zip.replace(/(\d{5})-?(\d{4})/, '$1-$2'))

// To be used along with the MaskedInput component
export const SHORT_ZIP_MASK_PATTERN = [/\d/, /\d/, /\d/, /\d/, /\d/]
export const LONG_ZIP_MASK_PATTERN = [/\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]
export const zipCodeMask = (val) =>
  // Use the 5-digit mask until the user enters a 6th character, so that we don't show the
  // hyphen and make the user think they have to enter the extended zip
  val.length > 5 ? LONG_ZIP_MASK_PATTERN : SHORT_ZIP_MASK_PATTERN

export const WEIGHT_MASK_PATTERN = [/\d/, /\d/, /\d/]
